import {
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	CardHeading,
	CheckboxField,
	Icon,
	IconButton,
	SingleSelectField,
	SubmitButton,
	isSuccess,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FieldArray, Form, Formik, useFormikContext } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { integrationService } from "../shared/api/integrationService";
import { useContactAttributeOptions } from "../shared/hooks/useContactAttributeOptions";
import { ContactAttributeOption } from "../shared/types";
import { DresoConfig, DresoConfigEntry, DresoField, IntegrationResource } from "../shared/types/integration";

// Maps each DresoField enum value to a translation key
const DRESO_FIELD_LABEL: Record<DresoField, string> = {
	[DresoField.DEFAULT_LOCALE]: "Default locale",
	[DresoField.CAREER_START]: "Career start",
	[DresoField.CAREER_START_ADJUSTED]: "Career start (adjusted)",
	[DresoField.CONFIDENTIAL_EMAIL]: "Confidential email",
	[DresoField.DISPLAY_BIRTHDAY]: "Display birthday",
	[DresoField.DOMAIN_ACCOUNT]: "Domain account",
	[DresoField.EMAIL]: "Email",
	[DresoField.EMPLOYEE_MAGAZINE_TYPE]: "Employee magazine type",
	[DresoField.ENTRY_GROUP]: "Entry group",
	[DresoField.ENTRY_TECHNICAL]: "Entry (technical)",
	[DresoField.FAX]: "Fax",
	[DresoField.FIRST_NAME]: "First name",
	[DresoField.GENDER]: "Gender",
	[DresoField.ID]: "Dreso ID",
	[DresoField.IMPORTED]: "Imported",
	[DresoField.LAST_WORKING_DAY]: "Last working day",
	[DresoField.LAST_NAME]: "Last name",
	[DresoField.LICENSE_PLATE_BUSINESS_CAR]: "License plate (business car)",
	[DresoField.LICENSE_PLATE_PRIVATE_CAR]: "License plate (private car)",
	[DresoField.MOBILE_PHONE]: "Mobile phone",
	[DresoField.PERSONNEL_NUMBER]: "Personnel number",
	[DresoField.PHONE]: "Phone",
	[DresoField.PREFERRED_FIRST_NAME]: "Preferred first name",
	[DresoField.PREFERRED_LAST_NAME]: "Preferred last name",
	[DresoField.RESIGNATION]: "Resignation",
	[DresoField.SALUTATION]: "Salutation",
	[DresoField.SIGNATURE_ALTERNATIVE_JOB_LEVEL_DE]: "Signature alternative job level (DE)",
	[DresoField.SIGNATURE_ALTERNATIVE_JOB_LEVEL_EN]: "Signature alternative job level (EN)",
	[DresoField.SIGNATURE_USE_JOB_LEVEL]: "Signature use job level",
	[DresoField.SIGNATURE_USE_JOB_LEVEL_CODE]: "Signature use job level code",
	[DresoField.SIGNATURE_USE_MOBILE_PHONE]: "Signature use mobile phone",
	[DresoField.TITLE]: "Title",
	[DresoField.ABBREVIATION]: "Abbreviation",
	[DresoField.ACADEMIC_TITLE]: "Academic title",
	[DresoField.ACTIVE]: "Active",
	[DresoField.ALLOWED_TO_DISPOSE]: "Allowed to dispose",
	[DresoField.EMPLOYEE_ID]: "Employee ID",
	[DresoField.EMPLOYEE_TYPE]: "Employee type",
	[DresoField.ENTRY_DATE]: "Entry date",
	[DresoField.EXIT_DATE]: "Exit date",
	[DresoField.FACTOR]: "Factor",
	[DresoField.FACTORED_STANDARD_HOURS_PER_MONTH]: "Factored standard hours per month",
	[DresoField.INTERNAL_SUPPORT]: "Internal support",
	[DresoField.PERSON]: "Person",
	[DresoField.STANDARD_HOURS_PER_DAY]: "Standard hours per day",
	[DresoField.STANDARD_HOURS_PER_MONTH]: "Standard hours per month",
	[DresoField.STATUS]: "Status",
	[DresoField.WRITING_HOURS]: "Writing hours",
};

const HARDCODED_DRESO_FIELDS: DresoField[] = [
	DresoField.FIRST_NAME,
	DresoField.LAST_NAME,
	DresoField.EMAIL,
	DresoField.DEFAULT_LOCALE,
];

interface DresoConfigFormEntry {
	dresoField: DresoField | null;
	contactAttributeId: number | null;
	isReference: boolean;
	isRequired: boolean;
}

interface FormValues {
	config: DresoConfigFormEntry[];
}

const DEFAULT_CONFIG: DresoConfigFormEntry[] = [];

const EMPTY_ENTRY: DresoConfigFormEntry = {
	dresoField: null,
	contactAttributeId: null,
	isReference: false,
	isRequired: false,
};

const MappingGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr auto auto auto;
	gap: ${({ theme }) => theme.spacing(2)};
	align-items: center;
`;

const ColumnHeader = styled.div`
	font-size: ${({ theme }) => theme.fonts.sizes.small};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
	padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const RemoveCell = styled.div`
	display: flex;
	align-items: center;
	padding-bottom: ${({ theme }) => theme.spacing(5)};
`;

const ReadOnlyCell = styled.div`
	font-size: ${({ theme }) => theme.fonts.sizes.normal};
	padding: ${({ theme }) => theme.spacing(1)} 0;
`;

const HARDCODED_ROWS: { dresoField: string; contactAttribute: string }[] = [
	{ dresoField: "First name", contactAttribute: "firstName" },
	{ dresoField: "Last name", contactAttribute: "lastName" },
	{ dresoField: "Email", contactAttribute: "Primary email" },
	{ dresoField: "Default locale", contactAttribute: "Locale" },
];

export function DresoConfigForm({ integration }: { integration: IntegrationResource }) {
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const { t } = useTranslation();

	const initialConfig: DresoConfigFormEntry[] =
		Array.isArray(integration.config) && integration.config.length > 0
			? (integration.config as DresoConfigFormEntry[])
			: DEFAULT_CONFIG;

	const onSubmit = async (values: FormValues) => {
		const validEntries: DresoConfigEntry[] = values.config
			.filter((e): e is DresoConfigEntry => e.dresoField !== null && e.contactAttributeId !== null)
			.map(({ dresoField, contactAttributeId, isReference, isRequired }) => ({
				dresoField,
				contactAttributeId,
				isReference,
				isRequired,
			}));

		const response = await integrationService.update(integration.id, validEntries as DresoConfig);

		if (isSuccess(response)) {
			await queryClient.invalidateQueries({ queryKey: integrationService.showKey(integration.id) });
			showToast({
				type: "success",
				title: t("Configuration"),
				body: t("Configuration was successfully saved."),
			});
		}
	};

	return (
		<Formik
			initialValues={{ config: initialConfig }}
			onSubmit={onSubmit}
			validate={(values) => {
				const mappingErrors = values.config.map((entry) => {
					if (entry.dresoField === null || entry.contactAttributeId === null) {
						return {
							dresoField: entry.dresoField === null ? t("Field is required") : undefined,
							contactAttributeId: entry.contactAttributeId === null ? t("Field is required") : undefined,
						};
					}
					return null;
				});

				if (mappingErrors.some(Boolean)) {
					return { config: mappingErrors };
				}

				return {};
			}}
		>
			{({ values }) => (
				<Form>
					<Card>
						<CardHeader>
							<CardHeading>{t("Configuration")}</CardHeading>
						</CardHeader>
						<CardBody>
							<FieldArray name="config">
								{({ push, remove }) => (
									<>
										<MappingGrid>
											<ColumnHeader>{t("Dreso field")}</ColumnHeader>
											<ColumnHeader>{t("Contact attribute")}</ColumnHeader>
											<ColumnHeader>{t("Reference")}</ColumnHeader>
											<ColumnHeader>{t("Required")}</ColumnHeader>
											<div />
											{HARDCODED_ROWS.map((row) => (
												<React.Fragment key={row.dresoField}>
													<ReadOnlyCell>{t(row.dresoField)}</ReadOnlyCell>
													<ReadOnlyCell>{t(row.contactAttribute)}</ReadOnlyCell>
													<div />
													<Icon $icon="check" />
													<div />
												</React.Fragment>
											))}
											{values.config.map((_, index) => (
												<MappingRow key={index} index={index} onRemove={() => remove(index)} />
											))}
										</MappingGrid>
										<IconButton
											type="button"
											icon="plus"
											color="primary"
											size="small"
											title={t("Add mapping")}
											onClick={() => push({ ...EMPTY_ENTRY })}
										/>
									</>
								)}
							</FieldArray>
						</CardBody>
						<CardFooter>
							<SubmitButton>{t("Save")}</SubmitButton>
						</CardFooter>
					</Card>
				</Form>
			)}
		</Formik>
	);
}

interface MappingRowProps {
	index: number;
	onRemove: () => void;
}

function MappingRow({ index, onRemove }: MappingRowProps) {
	const { t } = useTranslation();
	const { values } = useFormikContext<FormValues>();
	const { contactAttributeOptions } = useContactAttributeOptions();

	const currentEntry = values.config[index];

	const usedDresoFields = values.config
		.filter((_, i) => i !== index)
		.map((e) => e.dresoField)
		.filter((f): f is DresoField => f !== null);

	const usedAttributeIds = values.config
		.filter((_, i) => i !== index)
		.map((e) => e.contactAttributeId)
		.filter((id): id is number => id !== null);

	const dresoFieldOptions = (Object.values(DresoField) as DresoField[])
		.filter((f) => !HARDCODED_DRESO_FIELDS.includes(f))
		.filter((f) => !usedDresoFields.includes(f) || f === currentEntry.dresoField)
		.map((f) => ({ value: f, label: t(DRESO_FIELD_LABEL[f]) }));

	const filteredAttributeOptions = contactAttributeOptions.filter(
		(o) => !usedAttributeIds.includes(o.id) || o.id === currentEntry.contactAttributeId,
	);

	return (
		<>
			<SingleSelectField
				name={`config.${index}.dresoField`}
				options={dresoFieldOptions}
				getValue={(o: { value: DresoField; label: string }) => o.value}
				getLabel={(o: { value: DresoField; label: string }) => o.label}
				clearable
			/>
			<SingleSelectField
				name={`config.${index}.contactAttributeId`}
				options={filteredAttributeOptions}
				getValue={(o: ContactAttributeOption) => o.id}
				getLabel={(o: ContactAttributeOption) => o.name}
				clearable
			/>
			<CheckboxField name={`config.${index}.isReference`} label={t("Reference")} />
			<CheckboxField name={`config.${index}.isRequired`} label={t("Required")} />
			<RemoveCell>
				<IconButton size="small" icon="delete" title={t("Remove mapping")} onClick={onRemove} />
			</RemoveCell>
		</>
	);
}
